import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from 'react-bootstrap';
import { Orchard } from '../../services/sprayService';

interface Props {
  orchards: Orchard[];
  selectedIds: string[];
  onRemove: (id: string) => void;
}

const COLLAPSE_THRESHOLD = 12;

const OrchardSelectionSummary: React.FC<Props> = ({ orchards, selectedIds, onRemove }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedOrchards = useMemo(
    () =>
      orchards
        .filter(o => selectedSet.has(o._id))
        .sort((a, b) => a.orchardName.localeCompare(b.orchardName)),
    [orchards, selectedSet]
  );

  const totalHectares = useMemo(
    () => selectedOrchards.reduce((sum, o) => sum + (o.areaHectares ?? 0), 0),
    [selectedOrchards]
  );

  const needsCollapse = selectedOrchards.length > COLLAPSE_THRESHOLD;
  const visibleOrchards = needsCollapse && !expanded
    ? selectedOrchards.slice(0, COLLAPSE_THRESHOLD)
    : selectedOrchards;

  if (selectedOrchards.length === 0) {
    return <p className="text-muted">{t('spray.sessions.noOrchardsSelected')}</p>;
  }

  return (
    <div>
      <div className="d-flex flex-wrap gap-1 mb-1">
        {visibleOrchards.map(o => (
          <Badge
            key={o._id}
            bg="light"
            text="dark"
            className="border d-inline-flex align-items-center gap-1 fw-normal"
            data-testid="orchard-chip"
          >
            {o.orchardName} ({o.areaHectares != null ? `${o.areaHectares.toFixed(1)} ha` : '\u2014'})
            <button
              type="button"
              className="btn-close btn-close-sm ms-1"
              style={{ fontSize: '0.5rem' }}
              aria-label={t('spray.sessions.removeOrchard', { name: o.orchardName })}
              onClick={() => onRemove(o._id)}
            />
          </Badge>
        ))}
        {needsCollapse && (
          <Button
            variant="link"
            size="sm"
            className="p-0 align-self-center"
            onClick={() => setExpanded(prev => !prev)}
          >
            {expanded
              ? t('spray.sessions.showLess')
              : t('spray.sessions.showAll', { count: selectedOrchards.length })}
          </Button>
        )}
      </div>
      <small className="text-muted fw-bold">
        {t('spray.sessions.orchardSummary', {
          count: selectedOrchards.length,
          hectares: totalHectares.toFixed(1),
        })}
      </small>
    </div>
  );
};

export default OrchardSelectionSummary;
